import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Heart, Shield } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

export default function AssessmentResults() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [result, setResult] = useState(null);

  useEffect(() => {
    const r = sessionStorage.getItem("sh_last_result");
    if (r) setResult(JSON.parse(r));
    else nav("/assessment");
  }, [nav]);

  // If she's signed in (e.g. retaking), persist this snapshot to her history.
  useEffect(() => {
    if (user && result) {
      api.post("/assessment/save", result).catch(() => {});
    }
  }, [user, result]);

  if (!result) return null;
  const domains = Object.entries(result.domain_scores);

  return (
    <div className="min-h-screen bg-[#FCFAF6] p-6" data-testid="assessment-results">
      <div className="max-w-md mx-auto">
        <p className="text-xs uppercase tracking-[0.2em] text-[#C89B55] font-semibold mb-3">Your snapshot</p>
        <h1 className="font-display text-4xl mb-2">Here's where you are.</h1>
        <p className="text-[#291B2D]/60 mb-6">A starting point, sister — not a verdict.</p>

        <div data-testid="wellness-score-card" className="bg-[#291B2D] text-white rounded-3xl p-8 mb-6 card-soft">
          <p className="text-xs uppercase tracking-[0.2em] text-[#C89B55] font-semibold mb-3">Wellness Score</p>
          <div className="font-display text-6xl font-light">{result.wellness_score}</div>
          <div className="text-white/60 text-sm mt-2">out of 100</div>
        </div>

        {result.narrative && (
          <div data-testid="assessment-narrative" className="bg-white rounded-3xl p-6 card-soft mb-6 border-l-4 border-[#C89B55]">
            <p className="text-xs uppercase tracking-[0.2em] text-[#C89B55] font-semibold mb-2">Your reflection</p>
            <p className="text-[#291B2D]/85 leading-relaxed">{result.narrative}</p>
          </div>
        )}

        <div className="bg-white rounded-3xl p-6 card-soft mb-6">
          <h3 className="font-display text-lg mb-4">Your domains</h3>
          <div className="space-y-3">
            {domains.map(([id, d]) => (
              <div key={id} data-testid={`domain-${id}`}>
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="font-medium">{d.name}</span>
                  <span className="text-[#291B2D]/60 text-xs">{d.label}</span>
                </div>
                <div className="h-2 bg-[#F1EAE0] rounded-full overflow-hidden">
                  <div className="h-full bg-[#C89B55]" style={{ width: `${((d.max - d.score) / d.max) * 100}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-6">
          <div className="bg-white rounded-3xl p-5 card-soft">
            <p className="text-xs uppercase tracking-widest text-[#291B2D]/50">Gently nurture</p>
            <p className="font-display text-lg mt-1">{result.top_concern.name}</p>
          </div>
          <div className="bg-white rounded-3xl p-5 card-soft">
            <p className="text-xs uppercase tracking-widest text-[#291B2D]/50">Your strength</p>
            <p className="font-display text-lg mt-1">{result.strength.name}</p>
          </div>
        </div>

        <div className="bg-[#F7F2EA] rounded-3xl p-6 mb-6">
          <p className="text-xs uppercase tracking-[0.2em] text-[#291B2D]/60 font-semibold mb-2">Recommended next step</p>
          <p className="font-display text-xl mb-4">{result.next_step}</p>
          <Link to={user ? "/plans" : "/register"} data-testid="results-start-plan"
            className="inline-flex items-center gap-2 bg-[#291B2D] text-white rounded-full px-6 py-3 text-sm font-medium hover:bg-[#352238] transition-smooth">
            Start plan <ArrowRight size={14} />
          </Link>
        </div>

        <div className="space-y-3 mb-6">
          <Link to={user ? "/coach" : "/register"} data-testid="results-open-coach"
            className="flex items-center justify-between bg-white rounded-2xl p-4 card-soft">
            <div className="flex items-center gap-3"><Heart className="text-[#C89B55]" size={20} strokeWidth={1.5} /><span>Talk with your AI Coach</span></div>
            <ArrowRight size={16} className="text-[#291B2D]/40" />
          </Link>
          <Link to={user ? "/wellness" : "/register"} data-testid="results-daily-checkin"
            className="flex items-center justify-between bg-white rounded-2xl p-4 card-soft">
            <div className="flex items-center gap-3"><Shield className="text-[#3D6B52]" size={20} strokeWidth={1.5} /><span>Start your daily check-in</span></div>
            <ArrowRight size={16} className="text-[#291B2D]/40" />
          </Link>
        </div>

        {!user ? (
          <Link to="/register" data-testid="results-signup"
            className="block text-center bg-[#C89B55] text-[#291B2D] rounded-full py-4 font-semibold hover:bg-[#B38743] transition-smooth">
            Save your results — create free account
          </Link>
        ) : (
          <Link to="/home" data-testid="results-go-home"
            className="block text-center bg-[#C89B55] text-[#291B2D] rounded-full py-4 font-semibold hover:bg-[#B38743] transition-smooth">
            Go to my dashboard
          </Link>
        )}
        <p className="text-xs text-[#291B2D]/50 mt-6 text-center">Not diagnostic. Not therapy.</p>
      </div>
    </div>
  );
}
